import { useEffect, useState } from "react";
import { Heart, Star, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Loader } from "@/components/portal/ui";

export function FavoritesList() {
  const { t, L } = useI18n();
  const [providers, setProviders] = useState(null);
  const [favIds, setFavIds] = useState(new Set());
  const [onlyFav, setOnlyFav] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    const [p, f] = await Promise.all([
      api.get("/providers"),
      api.get("/favorites").catch(() => ({ data: [] })),
    ]);
    setProviders(p.data || []);
    setFavIds(new Set((f.data || []).map((x) => x.id)));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    const isFav = favIds.has(id);
    const next = new Set(favIds);
    isFav ? next.delete(id) : next.add(id);
    setFavIds(next);
    try {
      if (isFav) await api.delete(`/favorites/${id}`);
      else await api.post(`/favorites/${id}`);
    } catch (e) {
      setFavIds(favIds); // revert
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!providers) return <Loader />;

  const filtered = providers.filter((p) => {
    if (onlyFav && !favIds.has(p.id)) return false;
    if (query && !(p.business_name || "").toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };

  return (
    <div className="space-y-4" data-testid="favorites-list">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--text-muted)" }} />
          <input data-testid="favorites-search" placeholder={t("fav.discover")} className="w-full pl-9 pr-3 h-11 text-sm border outline-none" style={st}
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <button
          type="button"
          data-testid="favorites-only-toggle"
          onClick={() => setOnlyFav((v) => !v)}
          className="inline-flex items-center gap-1.5 h-11 px-4 text-sm font-medium border rounded-lg transition-colors"
          style={{ ...st, borderColor: onlyFav ? "var(--accent)" : "var(--border)", color: onlyFav ? "var(--accent)" : "var(--text)" }}
        >
          <Heart className="h-4 w-4" fill={onlyFav ? "currentColor" : "none"} /> {t("fav.onlyFavorites")} ({favIds.size})
        </button>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 flex flex-col items-center gap-3 text-center" data-testid="favorites-empty">
          <Heart className="h-8 w-8" style={{ color: "var(--accent)" }} />
          <span style={{ color: "var(--text-muted)" }}>{onlyFav ? t("fav.none") : t("fav.empty")}</span>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const isFav = favIds.has(p.id);
            return (
              <Card key={p.id} className="p-5 flex flex-col gap-2 relative kw-fade-up" data-testid={`favorite-card-${p.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  data-testid={`favorite-toggle-${p.id}`}
                  className="absolute top-4 right-4 transition-transform hover:scale-110"
                  aria-pressed={isFav}
                  aria-label={isFav ? t("fav.remove") : t("fav.add")}
                >
                  <Heart className="h-5 w-5" style={{ color: isFav ? "#ef4444" : "var(--text-muted)" }} fill={isFav ? "#ef4444" : "none"} />
                </button>
                <div className="font-heading text-lg font-bold pr-8" style={{ color: "var(--text)" }}>{typeof p.business_name === "object" ? L(p.business_name) : p.business_name}</div>
                {p.tagline && <p className="text-sm line-clamp-2" style={{ color: "var(--text-muted)" }}>{typeof p.tagline === "object" ? L(p.tagline) : p.tagline}</p>}
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(p.categories || []).slice(0, 3).map((c) => (
                    <span key={c.id} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{L(c.name)}</span>
                  ))}
                </div>
                {p.rating > 0 && (
                  <div className="flex items-center gap-1 text-sm mt-1" style={{ color: "var(--text)" }}>
                    <Star className="h-4 w-4" style={{ color: "var(--accent)" }} fill="var(--accent)" /> {p.rating}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
